import * as Dialog from '@radix-ui/react-dialog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from '@/components/Icons/ChevronLeft';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { Close } from '@/components/Icons/Close';
import { Download } from '@/components/Icons/Download';
import { useGalleryImage } from '@/hooks/useGalleryImage';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { GalleryFilmstrip } from '@/pages/DetailedNews/GalleryFilmstrip';
import { GalleryLightboxSlide } from '@/pages/DetailedNews/GalleryLightboxSlide';
import type { GalleryResponse } from '@/types/gallery';
import { triggerDownload } from '@/utils/file';

const SWIPE_THRESHOLD_PX = 50;

interface Props {
  gallery: GalleryResponse;
  initialIndex: number;
  isOpen: boolean;
  onBack?: () => void;
  onClose: () => void;
  onDownloaded: () => void;
}

export const GalleryLightbox: React.FC<Props> = ({
  gallery,
  initialIndex,
  isOpen,
  onBack,
  onClose,
  onDownloaded,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isJumping, setIsJumping] = useState(false);
  const touchStartXRef = useRef<number | null>(null);
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);

  useCloseOnBack(isOpen, onClose);

  const total = gallery.images.length;
  const currentImage = gallery.images[currentIndex];
  const fullImageUrl = useGalleryImage(currentImage?.id, null);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < total - 1;

  const goToIndex = useCallback(
    (nextIndex: number) => {
      const isNeighbour = Math.abs(nextIndex - currentIndex) === 1;

      setIsJumping(!isNeighbour);
      setCurrentIndex(nextIndex);
    },
    [currentIndex],
  );

  const handlePrev = useCallback(() => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((index) => Math.min(total - 1, index + 1));
  }, [total]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartXRef.current === null) {
      return;
    }

    const delta = event.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;

    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) {
      return;
    }

    if (delta < 0) {
      handleNext();
      return;
    }

    handlePrev();
  };

  const handleDownload = () => {
    if (!fullImageUrl) {
      return;
    }
    triggerDownload(fullImageUrl, currentImage.fileName);
    onDownloaded();
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isJumping) {
      return;
    }

    const frame = requestAnimationFrame(() => setIsJumping(false));

    return () => cancelAnimationFrame(frame);
  }, [isJumping]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, handlePrev, handleNext]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content
          ref={sheetRef}
          aria-describedby={undefined}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              onClose();
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#131313] outline-none md:bg-transparent md:p-4"
        >
          <Dialog.Title className="sr-only">
            {gallery.title ?? t('home.newsDetail.galleryTitle')}
          </Dialog.Title>

          <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4 pt-4 md:hidden">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label={t('global.back')}
                className="flex size-6 cursor-pointer items-center justify-center text-white"
              >
                <ChevronLeft className="size-5" />
              </button>
            )}
            {!onBack && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t('global.close')}
                className="flex size-6 cursor-pointer items-center justify-center text-white"
              >
                <Close className="size-5" strokeWidth="1.2" />
              </button>
            )}
            {total > 1 && (
              <span className="text-body-13 pointer-events-none absolute left-1/2 -translate-x-1/2 font-medium text-white">
                {t('home.newsDetail.galleryCounter', {
                  current: currentIndex + 1,
                  total,
                })}
              </span>
            )}
            <button
              type="button"
              onClick={handleDownload}
              aria-label={t('home.newsDetail.downloadLabel')}
              className="flex size-6 cursor-pointer items-center justify-center text-white"
            >
              <Download className="size-5" strokeWidth="1.2" />
            </button>
          </div>

          <div className="relative flex size-full max-h-full max-w-full flex-col md:h-[min(788px,calc(100svh-80px))] md:w-[min(1053px,calc(100vw-200px))] md:gap-5 md:rounded-xl md:border md:border-border md:bg-card md:p-5 md:pb-6 md:shadow-modal">
            {total > 1 && (
              <button
                type="button"
                onClick={handlePrev}
                disabled={!canPrev}
                aria-label="Previous"
                className="absolute top-1/2 right-full mr-4 hidden size-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-card text-foreground shadow-card transition-transform duration-150 ease-out active:scale-90 disabled:text-foreground-disabled md:flex"
              >
                <ChevronRight className="size-5 rotate-180" />
              </button>
            )}
            {total > 1 && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canNext}
                aria-label="Next"
                className="absolute top-1/2 left-full ml-4 hidden size-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-card text-foreground shadow-card transition-transform duration-150 ease-out active:scale-90 disabled:text-foreground-disabled md:flex"
              >
                <ChevronRight className="size-5" />
              </button>
            )}
            <header className="hidden shrink-0 items-center gap-3 md:flex">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  aria-label={t('global.back')}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-muted-foreground"
                >
                  <ChevronLeft className="size-5" />
                </button>
              )}
              <div className="ml-auto flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-2xl bg-info-surface px-3 py-2 text-caption-13 text-primary"
                >
                  <Download className="size-4" />
                  {t('home.newsDetail.downloadLabel')}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('global.close')}
                  className="flex size-6 cursor-pointer items-center justify-center text-muted-foreground"
                >
                  <Close className="size-5" />
                </button>
              </div>
            </header>

            <div
              {...swipeProps}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              className="relative h-full overflow-hidden max-md:touch-none md:h-auto md:min-h-0 md:flex-1 md:rounded-xl md:bg-card"
            >
              <div
                className={`flex h-full ${isJumping ? '' : 'transition-transform duration-350 ease-out'}`}
                style={{
                  transform: `translateX(-${currentIndex * 100}%)`,
                }}
              >
                {gallery.images.map((image) => (
                  <GalleryLightboxSlide key={image.id} image={image} />
                ))}
              </div>
            </div>

            {total > 1 && (
              <GalleryFilmstrip
                images={gallery.images}
                activeIndex={currentIndex}
                onSelect={goToIndex}
              />
            )}

            {total > 1 && (
              <footer className="hidden shrink-0 items-center justify-center gap-3 text-body-14 md:flex">
                <div className="flex items-center gap-1 text-foreground">
                  <span>{currentIndex + 1}</span>
                  <span className="text-subtle-foreground">/</span>
                  <span className="text-subtle-foreground">{total}</span>
                </div>
                {gallery.title && (
                  <span className="truncate text-foreground">
                    {gallery.title}
                  </span>
                )}
              </footer>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
